import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '../../api/user/user';
import { EmailAvailability } from '../../api/email-availability';
import { NotificationSettings } from 'src/app/api/notification/notification-settings';
import { EmailExclusion } from '../../api/system-config/email-exclusion';
import { PasswordResetResponse } from '../../api/password-reset-response';
import { TwoFactorStatus } from '../../api/user/two-factor-status';

@Injectable()
export class UserService {

  constructor(private httpClient: HttpClient) {

  }

  public isEmailAvailable(email: string): Observable<EmailAvailability> {
    return this.httpClient.get<EmailAvailability>('/api/public/validateEmail/' + email);
  }

  public toggleActive(userId: number): Observable<User> {
    return this.httpClient.put<User>(`/api/user/active/${userId}`, null);
  }

  public getIndividualCompanyUsers(): Observable<User[]> {
    return this.httpClient.get<User[]>('/api/user/individual/company');
  }

  public getNotificationSettings(userId: number): Observable<NotificationSettings> {
    return this.httpClient.get<NotificationSettings>(`/api/messaging/preferences/user/${userId}`);
  }

  public updateNotificationSettings(userId: number, settings: NotificationSettings): Observable<NotificationSettings> {
    return this.httpClient.put<NotificationSettings>(`/api/messaging/preferences/user/${userId}`, settings);
  }

  public getAllNonIndividualEmailsExclusions(): Observable<EmailExclusion[]> {
    return this.httpClient.get<EmailExclusion[]>('/api/user/non-individual/emails-exclusions');
  }

  public updatePermission(user: User): Observable<User> {
    return this.httpClient.put<User>(`/api/user/${user.id}/permissions`, user);
  }

  public resetUserPassword(email: string): Observable<PasswordResetResponse> {
    return this.httpClient.get<PasswordResetResponse>(`/api/public/request-password-reset/${email}`);
  }

  public saveSalesforceId(userId: number, salesforceId: string): Observable<User> {
    return this.httpClient.put<User>(`/api/user/${userId}/salesforce/${salesforceId}`, null);
  }

  public isOffline(email: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`/api/public/user/is-offline?email=${encodeURIComponent(email)}`);
  }

  /**
   * The second factors enrolled on an account. Internal-admin only, server side.
   *
   * An account with no login email (media vendors authenticate through non_login_email) has
   * no Firebase account, and the portal answers with an empty factor list rather than an
   * error - so an empty result means "nothing enrolled", not "lookup failed".
   */
  public getTwoFactorStatus(userId: number): Observable<TwoFactorStatus> {
    return this.httpClient.get<TwoFactorStatus>(`/api/user/${userId}/two-factor`);
  }

  /**
   * Removes every second factor from an account and revokes its sessions, so the user signs
   * in with a password alone and can re-enroll. This is the support path for someone an
   * enrolled factor is locking out - a lost phone, or Firebase's anti-abuse throttle, which
   * cannot be cleared and has no published duration (Bug 47480).
   *
   * It does NOT clear that throttle: it removes the need for an SMS rather than making one
   * possible. A user blocked mid-enrolment has nothing enrolled for this to clear.
   */
  public resetTwoFactor(userId: number): Observable<{ response: string }> {
    return this.httpClient.delete<{ response: string }>(`/api/user/${userId}/two-factor`);
  }

  /**
   * Flips the account's email-verified flag in Firebase. Internal-admin only, server side.
   *
   * A toggle, not a set - the endpoint reads the current value and writes its opposite. Only
   * ever called on an account that has just read as unverified, and the caller re-reads the
   * status afterwards rather than assuming which way it went (Bug 47867).
   *
   * The portal never sends verification emails, so this is the only way an account created
   * or activated without the flag can ever get it - and Firebase refuses second-factor
   * enrolment until it does.
   */
  public toggleEmailVerified(userId: number): Observable<{ response: string }> {
    return this.httpClient.put<{ response: string }>(`/api/user/${userId}/toggle-email-verified`, null);
  }
}
